import { ProcessServerConfigFunction } from 'filepond';
import { useState } from 'react';
import { QueryObserverResult } from 'react-query';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import {
  CaseByIdQuery,
  CaseDocumentationType,
  UploadSource,
  useCompleteAuditEntryMutation,
  useCreateAuditEntryMutation,
  useCreateCaseDocumentationUploadUrlMutation,
  useCreateCaseDocumentUploadMutation,
  useGetCaseDocumentQuery,
  useGetCaseDocumentUploadUrlMutation,
  useRemoveCaseDocumentationMutation,
  useUpdateCaseDocumentationMutation,
} from 'src/graphql';
import { AuditActions, SaveDocumentButtonStates } from 'src/types';
import { isError } from 'src/utils';
import { uploadToPresignedUrl } from 'src/utils/uploadToPresignedUrl';

export interface UseCaseDocuments {
  createDocumentEditorOpen: boolean;
  setCreateDocumentEditorOpen: (createDocumentEditorOpen: boolean) => void;
  selectedDocuments: CaseDocumentationType[];
  setSelectedDocuments: (selectedDocuments: CaseDocumentationType[]) => void;
  documentIsLoading: boolean;
  openDocument: CaseDocumentationType;
  setOpenDocumentId: (id: string | null) => void;
  handleRemoveDocuments: () => void;
  handleSaveDocument: (
    title: string,
    value: string,
    uploads: string[],
  ) => Promise<void>;
  handleOpenDocument: (doc: CaseDocumentationType) => Promise<void>;
  updateFile: ProcessServerConfigFunction;
  closeDocumentEditor: () => void;
  updateCaseDocumentation: ReturnType<
    typeof useUpdateCaseDocumentationMutation
  >;
  removeCaseDocumentation: ReturnType<
    typeof useRemoveCaseDocumentationMutation
  >;
  documentState: SaveDocumentButtonStates;
  setDocumentState: (documentState: SaveDocumentButtonStates) => void;
  activeTab: number;
  setActiveTab: (activeTab: number) => void;
  tempActiveTab: number;
  setTempActiveTab: (tempActiveTab: number) => void;
  selectActiveTab: (index: number) => void;
}

export const useCaseDocuments = (
  caseId: string,
  refetchDocuments: () => Promise<QueryObserverResult<CaseByIdQuery, unknown>>,
  setIsSaving: (saving: boolean) => void,
): UseCaseDocuments => {
  const [documentState, setDocumentState] = useState<SaveDocumentButtonStates>(
    SaveDocumentButtonStates.UNSAVED,
  );
  const [createDocumentEditorOpen, setCreateDocumentEditorOpen] =
    useState(false);
  const [openDocumentId, setOpenDocumentId] = useState<string | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<
    CaseDocumentationType[]
  >([]);
  const [activeTab, setActiveTab] = useState(0);
  const [tempActiveTab, setTempActiveTab] = useState(0);

  const updateCaseDocumentation = useUpdateCaseDocumentationMutation({
    onSuccess: () => {
      setDocumentState(SaveDocumentButtonStates.SAVED);
    },
    onError: () => {
      handleErrorToast({
        toastMessage: 'Oops, something went wrong. Please try again.',
      });
      setDocumentState(SaveDocumentButtonStates.UNSAVED);
    },
  });

  const removeCaseDocumentation = useRemoveCaseDocumentationMutation();

  const getUploadedCaseDocumentURL = useGetCaseDocumentUploadUrlMutation();

  const getUploadURL = useCreateCaseDocumentationUploadUrlMutation({
    onError: (error: Error) => {
      // Don't show toast for validation errors - Uppy's UI already shows them
      if (!error.message.includes('is not a supported format')) {
        handleErrorToast({
          toastMessage: `Failed to open file. Please try again`,
        });
      }
    },
  });

  const createDocumentUpload = useCreateCaseDocumentUploadMutation();

  const {
    data: openDocumentData,
    isLoading: documentIsLoading,
    refetch: refetchOpenDocument,
  } = useGetCaseDocumentQuery({
    caseDocumentId: openDocumentId,
  });

  const openDocument =
    (openDocumentData?.getCaseDocument as CaseDocumentationType) ?? null;

  const selectedDocumentIds = selectedDocuments.map((doc) => doc.id);
  const handleRemoveDocuments = async () => {
    await removeCaseDocumentation.mutateAsync({
      caseId: caseId,
      selectedDocumentIds: selectedDocumentIds,
    });
    await refetchDocuments();
    setSelectedDocuments([]);
  };

  const handleSaveDocument = async (
    title: string,
    value: string,
    uploads: string[],
  ) => {
    const newTitle = title === '' ? undefined : title;
    const result = await updateCaseDocumentation.mutateAsync({
      id: openDocumentId,
      caseId: caseId,
      title: newTitle,
      value,
      uploads,
    });

    if (!openDocumentId) {
      setOpenDocumentId(result.createOrUpdateCaseDocument.id);
    }

    await refetchDocuments();
    await refetchOpenDocument();
  };

  const createAuditEntry = useCreateAuditEntryMutation();
  const completeAuditEntry = useCompleteAuditEntryMutation();

  const handleOpenDocument = async (doc: CaseDocumentationType) => {
    setIsSaving(false);

    const auditEntry = await createAuditEntry.mutateAsync({
      auditAction: AuditActions.VIEW_CASE_DOCUMENT,
      auditData: {
        caseDocumentId: doc.id,
        caseId,
      },
      completed: false,
    });

    if (doc.s3path) {
      try {
        const {
          getCaseDocumentUploadURL: { url },
        } = await getUploadedCaseDocumentURL.mutateAsync({
          s3Path: doc.s3path,
        });

        if (auditEntry.createAuditEntry?.id) {
          completeAuditEntry.mutate({ id: auditEntry?.createAuditEntry?.id });
        }

        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (err) {
        handleErrorToast({
          toastMessage: `Failed to open file '${doc.title}'. Please try again`,
        });
      }
    } else {
      setOpenDocumentId(doc.id);
      setCreateDocumentEditorOpen(true);

      if (auditEntry.createAuditEntry?.id) {
        completeAuditEntry.mutate({ id: auditEntry?.createAuditEntry?.id });
      }
    }
  };

  const updateFile: ProcessServerConfigFunction = async (
    _fieldName,
    file,
    _metadata,
    load,
    error,
    progress,
    abort,
  ) => {
    const controller = new AbortController();

    try {
      const { createCaseDocumentUploadURL } = await getUploadURL.mutateAsync({
        type: file.type,
        filename: file.name,
        uploadSource: UploadSource.ReshubWorkspace,
        caseId,
        size: file.size,
      });

      const { url, s3path } = createCaseDocumentUploadURL;
      if (!url || !s3path) {
        return error('No upload URL available');
      }

      const resp = await uploadToPresignedUrl({
        file,
        url,
        tags: { uploadSource: UploadSource.ReshubWorkspace },
        onUploadProgress: (event) =>
          progress(true, event.loaded, event.total ?? 100),
        controller,
      });

      if (resp.status > 204) {
        handleErrorToast({
          toastMessage: `File '${file.name}' failed to upload. ${resp.status} - ${resp.data}`,
        });
        throw error(`File upload returned ${resp.status} - ${resp.data}`);
      }

      load(s3path);

      // Workspace upload: create upload tracking, then create document wrapper
      await createDocumentUpload.mutateAsync({
        caseId,
        s3path,
        mimeType: file.type,
        size: file.size,
        filename: file.name,
        uploadSource: UploadSource.ReshubWorkspace,
      });

      await updateCaseDocumentation.mutateAsync({
        caseId: caseId,
        title: file.name,
        s3path,
        mimeType: file.type,
        uploads: [s3path],
      });

      await refetchDocuments();

      handleSuccessToast({
        toastMessage: `File '${file.name}' successfully uploaded.`,
      });
    } catch (err) {
      const errorMessage = isError(err) ? err.message : String(err);

      // Don't show toast for validation errors - Uppy's inline error display handles them
      // Only show toast for actual upload/network errors
      if (!errorMessage.includes('is not a supported format')) {
        handleErrorToast({
          toastMessage: `File '${file.name}' failed to upload. Please try again.`,
        });
      }

      error('');
    }

    return {
      abort: () => {
        controller.abort();
        abort();
      },
    };
  };

  const closeDocumentEditor = async () => {
    await refetchDocuments();
    setSelectedDocuments([]);
    setCreateDocumentEditorOpen(false);
    setDocumentState(SaveDocumentButtonStates.UNSAVED);
    setOpenDocumentId(null);
  };

  const selectActiveTab = (index: number) => {
    if (activeTab === 1 && createDocumentEditorOpen) {
      setTempActiveTab(index);
      setIsSaving(true);
    } else {
      setIsSaving(false);
      setCreateDocumentEditorOpen(false);
      setTempActiveTab(index);
      setActiveTab(index);
    }
  };

  return {
    documentState,
    setDocumentState,
    createDocumentEditorOpen,
    setCreateDocumentEditorOpen,
    selectedDocuments,
    setSelectedDocuments,
    updateCaseDocumentation,
    removeCaseDocumentation,
    documentIsLoading,
    openDocument,
    setOpenDocumentId,
    handleRemoveDocuments,
    handleSaveDocument,
    handleOpenDocument,
    updateFile,
    closeDocumentEditor,
    activeTab,
    setActiveTab,
    tempActiveTab,
    setTempActiveTab,
    selectActiveTab,
  };
};
